import { CreatePartFinderBatchEntry, PartFinderBatchInputMethod } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useCreatePartFinderBatch } from '@/hooks/api/part-finder-batches/useCreatePartFinderBatch';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CsvIntake } from './intake/CsvIntake';
import { extractColumnEntries } from './intake/extractColumnEntries';
import { ParsedPartNumberFile, parsePartNumberFile } from './intake/parsePartNumberFile';
import { parsePastedPartNumbers } from './intake/parsePartNumbers';
import { MAX_PART_NUMBERS } from './intake/partNumberValidation';
import { PasteIntake } from './intake/PasteIntake';

const MAX_NAME_LENGTH = 512;

type IntakeTab = 'paste' | 'csv';

interface ActiveIntake {
  entries: CreatePartFinderBatchEntry[];
  inputMethod: PartFinderBatchInputMethod;
  sourceFilename?: string;
}

export interface NewBatchModalProperties {
  open: boolean;
  onClose: () => void;
}

export const NewBatchModal = ({ open, onClose }: NewBatchModalProperties) => {
  const [tab, setTab] = useState<IntakeTab>('paste');
  const [name, setName] = useState('');
  const [pasteRaw, setPasteRaw] = useState('');
  const [file, setFile] = useState<ParsedPartNumberFile>();
  const [parseError, setParseError] = useState<string>();
  const [isParsing, setIsParsing] = useState(false);
  const [submitError, setSubmitError] = useState<Error>();
  const parseToken = useRef(0);

  const { mutateAsync, isPending } = useCreatePartFinderBatch();
  const { showToast } = useToast();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const pasteParsed = useMemo(() => parsePastedPartNumbers(pasteRaw), [pasteRaw]);
  const csvExtraction = useMemo(
    () => (file ? extractColumnEntries(file.rows, file.suggestedColumnIndex) : undefined),
    [file]
  );

  const active = useMemo<ActiveIntake>(() => {
    if (tab === 'csv') {
      return {
        entries: csvExtraction?.entries ?? [],
        inputMethod: PartFinderBatchInputMethod.Csv,
        sourceFilename: file?.filename,
      };
    }
    return { entries: pasteParsed.acceptedEntries, inputMethod: PartFinderBatchInputMethod.Paste };
  }, [tab, csvExtraction, file, pasteParsed]);

  const acceptedCount = active.entries.length;
  const exceedsLimit = acceptedCount > MAX_PART_NUMBERS;
  const canSubmit = acceptedCount > 0 && !exceedsLimit && !isPending && !isParsing;

  const resetState = () => {
    parseToken.current += 1;
    setTab('paste');
    setName('');
    setPasteRaw('');
    setFile(undefined);
    setParseError(undefined);
    setIsParsing(false);
    setSubmitError(undefined);
  };

  const handleClose = () => {
    if (isPending) return;
    resetState();
    onClose();
  };

  const handleFileDropped = (dropped: File) => {
    const token = (parseToken.current += 1);
    setIsParsing(true);
    setParseError(undefined);
    parsePartNumberFile(dropped)
      .then(parsed => {
        if (parseToken.current === token) setFile(parsed);
      })
      .catch((error: unknown) => {
        if (parseToken.current !== token) return;
        setParseError(error instanceof Error ? error.message : 'Failed to parse file');
        setFile(undefined);
      })
      .finally(() => {
        if (parseToken.current === token) setIsParsing(false);
      });
  };

  const handleClearFile = () => {
    parseToken.current += 1;
    setFile(undefined);
    setParseError(undefined);
    setIsParsing(false);
  };

  const handleSubmit = async () => {
    setSubmitError(undefined);
    try {
      const trimmedName = name.trim();
      const response = await mutateAsync({
        entries: active.entries,
        name: trimmedName || undefined,
        inputMethod: active.inputMethod,
        sourceFilename: active.sourceFilename,
      });
      const { partFinderBatch, invalidEntries } = response.data;
      const created = partFinderBatch.breakdown.total;
      showToast({
        message:
          invalidEntries.length > 0
            ? `Batch started with ${created} part numbers · ${invalidEntries.length} skipped (invalid)`
            : `Batch started with ${created} part numbers`,
        type: invalidEntries.length > 0 ? 'warning' : 'success',
      });
      resetState();
      onClose();
      navigate(getPath(`part-finder/batches/${partFinderBatch.id}`));
    } catch (error) {
      setSubmitError(error instanceof Error ? error : new Error('Failed to start batch'));
    }
  };

  const apiError = getApiError(submitError, 'Failed to start batch');

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
      <DialogTitle>Find multiple part numbers</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_event, value: IntakeTab) => setTab(value)} sx={{ marginBottom: '16px' }}>
          <Tab value='paste' label='Paste' data-testid='intake-tab-paste' />
          <Tab value='csv' label='Upload CSV' data-testid='intake-tab-csv' />
        </Tabs>
        <ApiErrorAlert error={apiError} />
        <TextField
          value={name}
          onChange={event => setName(event.target.value)}
          label='Name (optional)'
          placeholder='e.g. Q2 hydraulics sourcing'
          fullWidth
          sx={{ marginBottom: '16px' }}
          slotProps={{
            htmlInput: { maxLength: MAX_NAME_LENGTH, 'aria-label': 'Batch name', 'data-testid': 'batch-name-input' },
          }}
        />
        {tab === 'paste' && (
          <PasteIntake value={pasteRaw} onChange={setPasteRaw} parsed={pasteParsed} exceedsLimit={exceedsLimit} />
        )}
        {tab === 'csv' && (
          <CsvIntake
            file={file}
            extraction={csvExtraction}
            isParsing={isParsing}
            error={parseError}
            exceedsLimit={exceedsLimit}
            onFileDropped={handleFileDropped}
            onClear={handleClearFile}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={() => {
            void handleSubmit();
          }}
          disabled={!canSubmit}
          variant='contained'
          color='primary'
          startIcon={isPending ? <CircularProgress size={16} color='inherit' /> : undefined}
          data-testid='batch-submit'
        >
          {isPending ? 'Finding…' : `Find ${acceptedCount}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
